import { useState, FC, Fragment, useEffect } from 'react';
import { useMediaQuery, Button, Divider, List, ListItem, ListItemText, Collapse, ListItemSecondaryAction, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { IRoleAssignment, IRoleCategory } from '../../../models';
import { ExpandMore, ExpandLess, Check, Add } from '@mui/icons-material';
import clsx from 'clsx';
import { CategoryPartListItem } from './CategoryPartListItem';

interface ICategoryListItem {
  index: number;
  parentKey?: string;
  category: IRoleCategory;
  parent?: IRoleCategory;
  parentSetIsAssigned?: any;
  roleCategories: IRoleCategory[];
  setRoleCategories: any;
  nested?: boolean;
  setPartIds: any;
  partIds: number[];
  roleAssignments: IRoleAssignment[];
  setRoleAssignments: any;
  roles: any;
}

export const CategoryListItem: FC<ICategoryListItem> = ({
  index,
  parentKey,
  category,
  parent,
  parentSetIsAssigned,
  roleCategories,
  setRoleCategories,
  nested,
  setPartIds,
  partIds,
  roleAssignments,
  setRoleAssignments,
  roles
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(!open);
  };
  const [isAssigned, setIsAssigned] = useState(category.assigned);
  const isMobile = useMediaQuery('(max-width: 960px)');
  // const [assignedRoles, setAssignedRoles] = useState(category.assignedRoles.length > 0 ? category.assignedRoles.map(role => role.name.trim()).join(', ') : null);

  useEffect(() => {
    setIsAssigned(category.assigned);
  }, [category.assigned]);

  const recurseCategoryUp = (value: boolean) => {
    category.assigned = value;

    if (category.assigned) {
      category.assignedRoles = roles;
    } else {
      category.assignedRoles = null;
    }
    if (parent) {
      // Assign Parent
      if (!parent.assigned && value) {
        parentSetIsAssigned(true);
      }
      // Unassign Parent
      else if (parent.assigned && (!parent.subCategories || parent.subCategories.every(c => !c.assigned)) && (!parent.parts || parent.parts.every(p => !p.assigned))) {
        parentSetIsAssigned(false);
        category.assignedRoles = null;
      }
    }
  };

  const recurseCategoryDown = (category: IRoleCategory, newValue: boolean) => {
    category.assigned = newValue;

    // Add Category parts
    if (category.parts) {
      if (newValue) {
        category.assignedRoles = roles;
        partIds = partIds.concat(category.parts.map(part => part.id));

        let roleAssignmentsCopy = [...roleAssignments];
        for (const assignment of roleAssignmentsCopy) {
          let partsAddedToArray = assignment.approvedPartIds.concat(partIds);
          assignment.approvedPartIds = partsAddedToArray.filter((item, pos) => partsAddedToArray.indexOf(item) === pos);
        }
        setRoleAssignments(roleAssignmentsCopy);
      } else {
        // Remove Category parts
        category.assignedRoles = null;
        partIds = partIds.filter(id => !category.parts.some(part => id === part.id));

        let roleAssignmentsCopy = [...roleAssignments];
        for (const assignment of roleAssignmentsCopy) {
          let partsRemovedFromArray = assignment.approvedPartIds.filter(id => !category.parts.some(part => id === part.id));
          assignment.approvedPartIds = partsRemovedFromArray;
        }
        setRoleAssignments(roleAssignmentsCopy);
      }
      setPartIds(partIds);
      category.parts.forEach(part => {
        part.assigned = newValue;
      });
    }

    if (category.subCategories) {
      category.subCategories.forEach(cat => {
        recurseCategoryDown(cat, newValue);
      });
    }
  };

  const clickCategory = () => {
    recurseCategoryDown(category, !category.assigned);

    if (parent) {
      recurseCategoryUp(category.assigned);
    }
  };

  return (
    <>
      <ListItem
        button
        classes={{
          root: category.subCategories || (category.parts && !category.subCategories) ? (nested ? classes.nestedListItemWithCats : classes.listItemWithCats) : classes.listItem
        }}
        onClick={category.subCategories && category.subCategories.length > 0 ? handleClick : null}
      >
        {(category.subCategories || category.parts) && (
          <IconButton
            color="default"
            className={classes.openButton}
            aria-label="open"
            onClick={() => {
              setOpen(!open);
            }}
            size="large"
          >
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
        <ListItemText
          primary={category.className}
          secondary={category.assignedRoles && category.assignedRoles.length > 0 && category.assignedRoles.map(role => role.name.trim()).join(', ')}
        />
        <ListItemSecondaryAction className={classes.buttonWrapper}>
          <Button
            startIcon={isAssigned ? <Check /> : <Add />}
            color="primary"
            variant={isAssigned ? 'contained' : 'text'}
            className={category.subCategories ? clsx(classes.editButton, isAssigned && classes.selected) : isAssigned ? classes.selected : undefined}
            onClick={clickCategory}
          >
            {isMobile ? '' : `${isAssigned ? 'Assigned' : 'Assign'}`}
          </Button>
        </ListItemSecondaryAction>
      </ListItem>
      <Divider />
      <Collapse in={open} timeout="auto" unmountOnExit className={classes.nested}>
        <List component="div" disablePadding>
          <>
            {category.subCategories &&
              category.subCategories.length > 0 &&
              category.subCategories.map((cat, i) => {
                const key = `${i}` + (parentKey ? `-${parentKey}` : '');
                return (
                  <Fragment key={key}>
                    <CategoryListItem
                      category={cat}
                      parent={category}
                      parentSetIsAssigned={recurseCategoryUp}
                      index={i}
                      setRoleCategories={setRoleCategories}
                      roleCategories={roleCategories}
                      setPartIds={setPartIds}
                      partIds={partIds}
                      parentKey={key}
                      roleAssignments={roleAssignments}
                      setRoleAssignments={setRoleAssignments}
                      roles={roles}
                    />
                  </Fragment>
                );
              })}
            {category.parts &&
              category.parts.map((part, partIndex) => {
                return (
                  <Fragment key={`${partIndex}-${index}`}>
                    <CategoryPartListItem
                      category={category}
                      parentSetIsAssigned={recurseCategoryUp}
                      topLevelIndex={index}
                      subListIndex={partIndex}
                      part={part}
                      setPartIds={setPartIds}
                      partIds={partIds}
                      roleCategories={roleCategories}
                      setRoleCategories={setRoleCategories}
                      roleAssignments={roleAssignments}
                      setRoleAssignments={setRoleAssignments}
                      roles={roles}
                    />
                  </Fragment>
                );
              })}
          </>
        </List>
      </Collapse>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper
  },
  nested: {
    paddingLeft: theme.spacing(2)
  },
  listItem: {
    paddingTop: 2,
    paddingBottom: 2
  },
  listItemWithCats: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: theme.spacing(1)
  },
  nestedListItemWithCats: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: theme.spacing(2)
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    marginRight: 8,
    padding: 0
  },
  openButton: {
    padding: 0
  },
  selected: {
    fontWeight: 'bold',
    padding: `2px 8px`,
    borderRadius: 5,
    marginRight: 0
  }
}));
